import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { McpConnectorUI } from '../components/dashboard/user-settings/mcp-connectors/types';
import type { RootState } from '.';

interface McpConnectorsState {
  connectors: McpConnectorUI[] | undefined;
  isLoading: boolean;
  // Local-only "Failed connect attempt" tracking — not persisted on the BE.
  // Stores connector ids whose latest user-initiated connect did not produce a connectionId.
  failedConnectorIds: string[];
}

const initialState: McpConnectorsState = {
  connectors: undefined,
  isLoading: false,
  failedConnectorIds: [],
};

export const mcpConnectorsSlice = createSlice({
  name: 'mcpConnectors',
  initialState,
  reducers: {
    setConnectors: (state, action: PayloadAction<McpConnectorUI[]>) => {
      state.connectors = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    addConnector: (state, action: PayloadAction<McpConnectorUI>) => {
      state.connectors = [...(state.connectors ?? []), action.payload];
    },
    removeConnector: (state, action: PayloadAction<string>) => {
      state.connectors = state.connectors?.filter((c) => c.id !== action.payload);
      state.failedConnectorIds = state.failedConnectorIds.filter((id) => id !== action.payload);
    },
    clearConnectorConnection: (state, action: PayloadAction<string>) => {
      const cleared = state.connectors?.find((c) => c.connectionId === action.payload);
      state.connectors = state.connectors?.map((c) =>
        c.connectionId === action.payload ? { ...c, connectionId: undefined } : c,
      );
      if (cleared) {
        state.failedConnectorIds = state.failedConnectorIds.filter((id) => id !== cleared.id);
      }
    },
    markConnectorFailed: (state, action: PayloadAction<string>) => {
      if (!state.failedConnectorIds.includes(action.payload)) {
        state.failedConnectorIds.push(action.payload);
      }
    },
    clearConnectorFailed: (state, action: PayloadAction<string>) => {
      state.failedConnectorIds = state.failedConnectorIds.filter((id) => id !== action.payload);
    },
  },
});

export const {
  setConnectors,
  setIsLoading,
  addConnector,
  removeConnector,
  clearConnectorConnection,
  markConnectorFailed,
  clearConnectorFailed,
} = mcpConnectorsSlice.actions;

export const selectConnectors = (state: RootState) => state.mcpConnectors.connectors;
export const selectMcpConnectionsIsLoading = (state: RootState) => state.mcpConnectors.isLoading;
export const selectFailedConnectorIds = (state: RootState) =>
  state.mcpConnectors.failedConnectorIds;

export default mcpConnectorsSlice.reducer;
